import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';
import { useSelector } from 'react-redux';

const LicenseAgreement = () => {
  const navigate = useNavigate();
  const signatureRef = useRef(null);
  const [agreementData, setAgreementData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [signatureEmpty, setSignatureEmpty] = useState(true);

  const user = useSelector((state) => state.auth.user);
  const guardianId = user?.userable_id;

  useEffect(() => {
    fetchAgreementData();
  }, []);

  const fetchAgreementData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/license-agreement/${guardianId}`);
      setAgreementData(response.data.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching license agreement:', err);
      setError(err.response?.data?.message || 'Failed to load license agreement');
      setLoading(false);
    }
  };

  const clearSignature = () => {
    signatureRef.current.clear();
    setSignatureEmpty(true);
  };

  const handleSignatureEnd = () => {
    setSignatureEmpty(signatureRef.current.isEmpty());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (signatureEmpty) {
      setError('Please provide your signature');
      return;
    }

    if (!agreedToTerms) {
      setError('You must agree to the terms and conditions');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      // Get signature as base64 data URL
      const signatureData = signatureRef.current.toDataURL();

      const response = await axios.post(`/api/license-agreement/${guardianId}/sign`, {
        signatureData,
        agreedToTerms: true,
      });

      // Success - redirect to dashboard with success message
      navigate('/guardians/dashboard', {
        state: {
          message: 'Congratulations! Your license agreement has been signed. You are now placed in your property.',
          type: 'success',
        },
      });
    } catch (err) {
      console.error('Error signing license agreement:', err);
      setError(err.response?.data?.message || 'Failed to sign license agreement');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading license agreement...</p>
        </div>
      </div>
    );
  }

  if (error && !agreementData) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/guardians/dashboard')}>
          Return to Dashboard
        </button>
      </div>
    );
  }

  const { guardian, property, rent, initialPayment } = agreementData || {};

  return (
    <div className="container mt-4 mb-5">
      <div className="row">
        <div className="col-lg-10 mx-auto">
          <div className="card shadow">
            <div className="card-header bg-primary text-white">
              <h3 className="mb-0">License Agreement</h3>
            </div>
            <div className="card-body">
              {/* Company Header */}
              <div className="text-center mb-4">
                <h4 className="fw-bold">GLOBAL GUARDIANS</h4>
                <p className="mb-0">Property Guardian License Agreement</p>
              </div>

              {/* Agreement Parties */}
              <div className="row mb-4">
                <div className="col-md-6">
                  <h5 className="fw-bold">Licensor:</h5>
                  <p className="mb-1">Global Guardians Ltd</p>
                  <p className="mb-1">123 Business Street</p>
                  <p className="mb-0">London, UK</p>
                </div>
                <div className="col-md-6">
                  <h5 className="fw-bold">Licensee (Guardian):</h5>
                  <p className="mb-1">
                    {guardian?.forename} {guardian?.surname}
                  </p>
                  <p className="mb-1">{guardian?.email}</p>
                  <p className="mb-0">{guardian?.mobile}</p>
                </div>
              </div>

              <hr />

              {/* Property Details */}
              <div className="mb-4">
                <h5 className="fw-bold">Property Details:</h5>
                <p className="mb-1">
                  <strong>Address:</strong> {property?.full_address || 'N/A'}
                </p>
                <p className="mb-1">
                  <strong>Property Type:</strong> {property?.property_type_name || 'N/A'}
                </p>
                <p className="mb-0">
                  <strong>License Start Date:</strong>{' '}
                  {rent?.date_start
                    ? new Date(rent.date_start).toLocaleDateString('en-GB')
                    : 'N/A'}
                </p>
              </div>

              <hr />

              {/* Financial Terms */}
              <div className="mb-4">
                <h5 className="fw-bold">Financial Terms:</h5>
                <p className="mb-1">
                  <strong>Monthly License Fee:</strong> £
                  {rent?.weekly_amount ? (parseFloat(rent.weekly_amount) * 52 / 12).toFixed(2) : '0.00'}
                </p>
                <p className="mb-1">
                  <strong>Initial Payment:</strong> £
                  {initialPayment?.amount_due || '0.00'}
                </p>
                <p className="mb-0">
                  <strong>Payment Status:</strong>{' '}
                  <span
                    className={`badge ${
                      initialPayment?.status === 'paid' ? 'bg-success' : 'bg-warning'
                    }`}
                  >
                    {initialPayment?.status || 'Pending'}
                  </span>
                </p>
              </div>

              <hr />

              {/* Terms and Conditions */}
              <div className="mb-4">
                <h5 className="fw-bold">Terms and Conditions:</h5>
                <div className="terms-content" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <ol className="ps-3">
                    <li className="mb-2">
                      <strong>License Grant:</strong> The Licensor grants the Licensee a
                      non-exclusive license to occupy the Property for residential purposes only.
                    </li>
                    <li className="mb-2">
                      <strong>Security Responsibilities:</strong> The Licensee agrees to provide
                      security services by maintaining a physical presence at the Property and
                      reporting any issues to the Licensor.
                    </li>
                    <li className="mb-2">
                      <strong>License Fees:</strong> The Licensee agrees to pay the monthly license
                      fee as specified above, payable in advance on the 1st of each month.
                    </li>
                    <li className="mb-2">
                      <strong>Property Condition:</strong> The Licensee agrees to keep the Property
                      in good condition and report any maintenance issues promptly.
                    </li>
                    <li className="mb-2">
                      <strong>Access Rights:</strong> The Licensor reserves the right to access the
                      Property with 24 hours' notice for inspections and maintenance.
                    </li>
                    <li className="mb-2">
                      <strong>Termination:</strong> Either party may terminate this agreement with 28
                      days' written notice. The Licensor may terminate immediately in case of breach.
                    </li>
                    <li className="mb-2">
                      <strong>Prohibited Activities:</strong> The Licensee shall not sublet, assign,
                      or allow unauthorized persons to occupy the Property.
                    </li>
                    <li className="mb-2">
                      <strong>Insurance:</strong> The Licensee is responsible for insuring their own
                      possessions. The Licensor maintains building insurance.
                    </li>
                    <li className="mb-2">
                      <strong>Utilities:</strong> All utility costs are included in the license fee
                      unless otherwise specified.
                    </li>
                    <li className="mb-2">
                      <strong>Governing Law:</strong> This agreement is governed by the laws of
                      England and Wales.
                    </li>
                  </ol>
                </div>
              </div>

              <hr />

              {/* Signature Section */}
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <h5 className="fw-bold">Your Signature:</h5>
                  <p className="text-muted small">
                    Please sign in the box below using your mouse or touchscreen
                  </p>

                  <div
                    className="signature-pad border border-2 rounded"
                    style={{ backgroundColor: '#f8f9fa' }}
                  >
                    <SignatureCanvas
                      ref={signatureRef}
                      canvasProps={{
                        width: 500,
                        height: 200,
                        className: 'signature-canvas',
                        style: { width: '100%', height: '200px' },
                      }}
                      onEnd={handleSignatureEnd}
                    />
                  </div>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary mt-2"
                    onClick={clearSignature}
                  >
                    Clear Signature
                  </button>
                </div>

                {/* Agreement Checkbox */}
                <div className="form-check mb-4">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="agreeTerms"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="agreeTerms">
                    I have read, understood, and agree to the terms and conditions of this License
                    Agreement
                  </label>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                {/* Submit Buttons */}
                <div className="d-flex gap-3">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    disabled={submitting || signatureEmpty || !agreedToTerms}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Signing Agreement...
                      </>
                    ) : (
                      'Sign Agreement'
                    )}
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-lg"
                    onClick={() => navigate('/guardians/dashboard')}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseAgreement;
